const DISPLAY_INDEX_ASSET_PATTERN = /(?:src|href)=["']([^"']*\/assets\/index-[^"']+\.(?:js|css)(?:[?#][^"']*)?)["']/g;

type AssetFetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type DisplayFrontendAssetRefreshOptions = {
  documentRef: Document;
  fetcher?: AssetFetcher;
  reload: () => void;
  rootPath?: string;
};

export function extractDisplayAssetSignature(html: string): string | null {
  const assets = new Set<string>();

  for (const match of html.matchAll(DISPLAY_INDEX_ASSET_PATTERN)) {
    const asset = normalizeDisplayAssetPath(match[1]);
    if (asset) {
      assets.add(asset);
    }
  }

  return formatAssetSignature(assets);
}

export function getCurrentDisplayAssetSignature(documentRef: Document): string | null {
  const assets = new Set<string>();
  const elements = documentRef.querySelectorAll<HTMLLinkElement | HTMLScriptElement>('link[href*="/assets/index-"],script[src*="/assets/index-"]');

  elements.forEach((element) => {
    const asset = normalizeDisplayAssetPath(element instanceof HTMLLinkElement ? element.href : element.src);
    if (asset) {
      assets.add(asset);
    }
  });

  return formatAssetSignature(assets);
}

export function shouldReloadForDisplayAssetSignature(currentSignature: string | null, latestSignature: string | null): boolean {
  return Boolean(currentSignature && latestSignature && currentSignature !== latestSignature);
}

export async function fetchLatestDisplayAssetSignature(fetcher: AssetFetcher = fetch, rootPath = "/"): Promise<string | null> {
  const response = await fetcher(rootPath, {
    cache: "no-store",
    headers: {
      accept: "text/html",
      "cache-control": "no-cache"
    }
  });

  if (!response.ok) {
    return null;
  }

  return extractDisplayAssetSignature(await response.text());
}

export async function checkDisplayFrontendAssetRefresh({
  documentRef,
  fetcher = fetch,
  reload,
  rootPath = "/"
}: DisplayFrontendAssetRefreshOptions): Promise<boolean> {
  const currentSignature = getCurrentDisplayAssetSignature(documentRef);
  const latestSignature = await fetchLatestDisplayAssetSignature(fetcher, rootPath);

  if (!shouldReloadForDisplayAssetSignature(currentSignature, latestSignature)) {
    return false;
  }

  reload();
  return true;
}

function normalizeDisplayAssetPath(rawPath: string): string | null {
  const pathOnly = rawPath.split(/[?#]/)[0];
  const match = pathOnly.match(/\/assets\/index-[^/]+\.(?:js|css)$/);
  return match?.[0] ?? null;
}

function formatAssetSignature(assets: Set<string>): string | null {
  if (assets.size === 0) {
    return null;
  }

  return [...assets].sort().join("|");
}
